// SPDX-License-Identifier: AGPL-3.0-or-later

import TaskMentions from '@app/features/tasks/state/TaskMentions';
import {Routes} from '@app/app/Routes';
import {ChatsHeading} from '@app/features/app/components/layout/pioneer/ChatsHeading';
import styles from '@app/features/app/components/layout/pioneer/PioneerSidebar.module.css';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {useLocation} from '@app/features/platform/components/router/RouterReact';
import {ComponentBus} from '@app/features/platform/utils/ComponentBus';
import QuickSwitcher from '@app/features/search/state/QuickSwitcher';
import Tasks from '@app/features/tasks/state/Tasks';
import TasksTab, {type TasksTabName} from '@app/features/tasks/state/TasksTab';
import {
	ACTIVITY_DESCRIPTOR,
	MY_TODOS_DESCRIPTOR,
	SEARCH_DESCRIPTOR,
	TEAM_BOARD_DESCRIPTOR,
} from '@app/features/tasks/utils/TaskMessageDescriptors';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import Users from '@app/features/user/state/Users';
import {useLingui} from '@lingui/react/macro';
import {BellSimpleIcon, CheckSquareIcon, KanbanIcon, MagnifyingGlassIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** Search, Activity, My to-dos and Team board at the top of the sidebar. */
export const PioneerSidebarNav = observer(({guildId}: {guildId: string}) => {
	const {i18n} = useLingui();
	const {pathname} = useLocation();
	const userId = Users.currentUser?.id ?? '';
	const onTasks = pathname === Routes.guildTasks(guildId);
	const myOpen = Tasks.getMyTasks(guildId, userId).filter((task) => task.status !== 'done').length;
	const allOpen = Tasks.getGuildTasks(guildId).filter((task) => task.status !== 'done').length;

	// In a chat, search its messages and files; anywhere else, jump to a chat or person.
	const inChat = Routes.isChannelRoute(pathname) && pathname.split('/').length >= 4 && !onTasks;
	const searchKey = `${isMac ? '⌘' : 'Ctrl '}${inChat ? 'F' : 'K'}`;
	const search = () => {
		if (inChat) ComponentBus.dispatch('MESSAGE_SEARCH_OPEN');
		else QuickSwitcher.show();
	};

	const openTasks = (tab: TasksTabName) => {
		TasksTab.setTab(tab);
		if (!onTasks) RouterUtils.transitionTo(Routes.guildTasks(guildId));
	};

	const rows = [
		{
			key: 'activity',
			icon: BellSimpleIcon,
			label: i18n._(ACTIVITY_DESCRIPTOR),
			on: pathname === Routes.MENTIONS,
			// New @ tags in to-do comments; chat mentions keep their own badges.
			count: TaskMentions.unseenCount,
			go: () => RouterUtils.transitionTo(Routes.MENTIONS),
		},
		{
			key: 'mine',
			icon: CheckSquareIcon,
			label: i18n._(MY_TODOS_DESCRIPTOR),
			on: onTasks && TasksTab.tab === 'mine',
			count: myOpen,
			go: () => openTasks('mine'),
		},
		{
			key: 'board',
			icon: KanbanIcon,
			label: i18n._(TEAM_BOARD_DESCRIPTOR),
			on: onTasks && TasksTab.tab === 'board',
			count: allOpen,
			go: () => openTasks('board'),
		},
	];

	// On a phone the bottom tabs carry Search, Activity and To-dos, and the header says Chats.
	if (MobileLayout.enabled) return null;
	return (
		<div className={styles.nav} data-flx="app.pioneer.pioneer-sidebar-nav.nav">
			<button
				type="button"
				className={styles.search}
				onClick={search}
				data-flx="app.pioneer.pioneer-sidebar-nav.search.button"
			>
				<MagnifyingGlassIcon
					size={16}
					aria-hidden="true"
					data-flx="app.pioneer.pioneer-sidebar-nav.magnifying-glass-icon"
				/>
				{i18n._(SEARCH_DESCRIPTOR)}
				<span className={styles.searchKey} data-flx="app.pioneer.pioneer-sidebar-nav.search-key">
					{searchKey}
				</span>
			</button>
			<div className={styles.rows} data-flx="app.pioneer.pioneer-sidebar-nav.rows">
				{rows.map((row) => {
					const Icon = row.icon;
					return (
						<button
							key={row.key}
							type="button"
							className={clsx(styles.row, row.on && styles.rowOn)}
							aria-current={row.on ? 'page' : undefined}
							onClick={row.go}
							data-flx="app.pioneer.pioneer-sidebar-nav.row.go.button"
						>
							<span className={styles.lead} data-flx="app.pioneer.pioneer-sidebar-nav.lead">
								<Icon size={16} aria-hidden="true" data-flx="app.pioneer.pioneer-sidebar-nav.icon" />
							</span>
							{row.label}
							{row.count != null && row.count > 0 && (
								<span className={styles.count} data-flx="app.pioneer.pioneer-sidebar-nav.count">
									{row.count}
								</span>
							)}
						</button>
					);
				})}
			</div>
			<ChatsHeading guildId={guildId} data-flx="app.pioneer.pioneer-sidebar-nav.chats-heading" />
		</div>
	);
});
